export const REQUIRED_EXPERIMENTAL_FIELDS = [
  'temperature_c',
  'viscosity_mpa_s',
  'total_length_cm',
  'post_detector_length_cm',
  'capillary_diam_um',
  'injection_pressure_mbar',
  'injection_time_s',
  'mobilizing_pressure_mbar',
];

export function emptyExperimentalFields() {
  return REQUIRED_EXPERIMENTAL_FIELDS.reduce((acc, key) => {
    acc[key] = '';
    return acc;
  }, {});
}

export function hasCustomExperimental(fields = {}) {
  return Object.values(fields).some((value) => {
    if (value === null || value === undefined) return false;
    return String(value).trim() !== '';
  });
}

export function findMissingExperimentalFields(fields = {}) {
  return REQUIRED_EXPERIMENTAL_FIELDS.filter((field) => {
    const value = fields[field];
    const trimmed =
      value === null || value === undefined ? '' : String(value).trim();
    const num = Number(value);
    return trimmed === '' || Number.isNaN(num);
  });
}

export function formatMissingMessage(missing = []) {
  return `Please provide numeric values for: ${missing
    .map((f) => f.replace(/_/g, ' '))
    .join(', ')}`;
}

function safeNumber(value) {
  if (value === null || value === undefined) return undefined;
  const trimmed = String(value).trim();
  if (trimmed === '') return undefined;
  const num = Number(trimmed);
  if (Number.isNaN(num)) return undefined;
  return num;
}

export function buildSetupPayload(fields = {}, name, extra = {}) {
  const payload = { ...extra };
  const nameValue = (name || '').trim();
  if (nameValue) payload.name = nameValue;

  const maybeAppend = (key) => {
    const val = safeNumber(fields[key]);
    if (val !== undefined) {
      payload[key] = val;
    }
  };

  maybeAppend('temperature_c');
  maybeAppend('viscosity_mpa_s');
  maybeAppend('total_length_cm');
  maybeAppend('post_detector_length_cm');
  maybeAppend('capillary_diam_um');
  maybeAppend('injection_pressure_mbar');
  maybeAppend('injection_time_s');
  maybeAppend('mobilizing_pressure_mbar');

  return payload;
}

export function mapSetupDetailToFields(detail) {
  if (!detail) return emptyExperimentalFields();
  return {
    temperature_c:
      detail.temperature_c ??
      (detail.temperature_k != null ? detail.temperature_k - 273.15 : ''),
    viscosity_mpa_s:
      detail.viscosity_mpa_s ??
      (detail.viscosity_pa_s != null ? detail.viscosity_pa_s * 1000 : ''),
    total_length_cm:
      detail.total_length_cm ??
      (detail.total_length_m != null ? detail.total_length_m * 100 : ''),
    post_detector_length_cm:
      detail.post_detector_length_cm ??
      (detail.post_detector_length_m != null
        ? detail.post_detector_length_m * 100
        : detail.post_length_m != null
          ? detail.post_length_m * 100
          : ''),
    capillary_diam_um:
      detail.capillary_diam_um ??
      (detail.capillary_diam_m != null ? detail.capillary_diam_m * 1e6 : ''),
    injection_pressure_mbar:
      detail.injection_pressure_mbar ??
      (detail.injection_pressure_pa != null
        ? detail.injection_pressure_pa / 100
        : ''),
    injection_time_s: detail.injection_time_s ?? '',
    mobilizing_pressure_mbar:
      detail.mobilizing_pressure_mbar ??
      (detail.mobilizing_pressure_pa != null
        ? detail.mobilizing_pressure_pa / 100
        : ''),
  };
}

export function extractApiError(
  err,
  fallback = 'Something went wrong. Please try again.'
) {
  const data = err?.response?.data;
  if (typeof data === 'string') return data;
  if (data?.detail) return data.detail;
  if (data && typeof data === 'object') {
    const firstFieldError = Object.values(data).find(
      (value) => Array.isArray(value) && value.length && typeof value[0] === 'string'
    );
    if (firstFieldError) return firstFieldError[0];
    // DRF nested serializer/admission errors retain their field path.
    const firstNestedError = (value, path = []) => {
      if (typeof value === 'string' && value) {
        return path.length ? `${path.join('.')}: ${value}` : value;
      }
      if (Array.isArray(value)) {
        for (const item of value) {
          const message = firstNestedError(item, path);
          if (message) return message;
        }
      } else if (value && typeof value === 'object') {
        for (const [field, item] of Object.entries(value)) {
          const message = firstNestedError(item, [...path, field]);
          if (message) return message;
        }
      }
      return null;
    };
    const nestedError = firstNestedError(data);
    if (nestedError) return nestedError;
  }
  if (err?.message) return err.message;
  return fallback;
}
